import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ErrorMessage } from '../utils/error-message.util';
import { UtilityService } from './utility.service';

/** The same message again within this window is dropped. */
export const ERROR_TOAST_DEDUPE_MS = 8000;
export const ERROR_TOAST_LIFE_MS = 8000;

/**
 * One calm toast per distinct failure. Embedded in HCP, the toast goes to
 * HCP's snackbar: the iframe is sized to its content, so a fixed toast in
 * here could sit off screen. Standalone, it uses the app's own <p-toast>.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorToastService {
  private readonly messageService = inject(MessageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly utilityService = inject(UtilityService);

  private readonly lastShown = new Map<string, number>();

  show(error: ErrorMessage): void {
    const message = 'text' in error ? error.text : this.translate(error);
    const now = Date.now();
    const shownAt = this.lastShown.get(message);
    if (shownAt !== undefined && now - shownAt < ERROR_TOAST_DEDUPE_MS) return;
    this.lastShown.set(message, now);

    if (window.parent !== window) {
      this.utilityService.emitToast({ message, isSuccess: false });
      return;
    }
    this.messageService.add({
      severity: 'error',
      detail: message,
      life: ERROR_TOAST_LIFE_MS,
      closable: true
    });
  }

  private translate({ key, what }: { key: string; what?: string }): string {
    const name = what ? this.translocoService.translate(what) : undefined;
    return this.translocoService.translate(key, { what: name });
  }
}
